import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertCircle, Check, Clock, ImageIcon, Loader2, Video } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MultiSelectPopover } from "@/components/ui/multi-select-popover";
import { WhatsAppPhone } from "@/components/internal/whatsapp/preview/WhatsAppPhone";
import { cn } from "@/utils/cn";
import { automationSetupQueryKey, createQuickAutomation, fetchAutomationSetup } from "../packagesApi";
import { PrimaryButton, SecondaryButton } from "./visuals";

/**
 * The hearing reminder or the birthday message, in one compact dialog: the
 * WhatsApp that sends (only when there is more than one), a ready-made text
 * the firm can rewrite (the client's name and the other data inserted as
 * [Nome do cliente]...), an optional image or video on top and, for the
 * reminder, which appointments. The hour is fixed. The template is created on Meta and the
 * automation turns on by itself once Meta approves it.
 *
 * When the legal system has no compact setup (`supported: false`), the dialog
 * is not shown: `onUnsupported` opens the full automation screen instead.
 */

const COPY = {
  reminder: {
    title: "Lembrete de audiências",
    create: "Criar lembrete",
    on: "O lembrete está ligado",
    waits: "O lembrete liga sozinho quando a Meta aprovar.",
  },
  birthday: {
    title: "Parabéns no aniversário",
    create: "Criar mensagem",
    on: "A mensagem está ligada",
    waits: "A mensagem liga sozinha quando a Meta aprovar.",
  },
};

const HEADERS = [
  { id: "none", label: "Só texto" },
  { id: "image", label: "Imagem", icon: ImageIcon, accept: "image/jpeg,image/png", hint: "JPG ou PNG, até 5 MB" },
  { id: "video", label: "Vídeo", icon: Video, accept: "video/mp4", hint: "MP4, até 16 MB" },
];

function Segmented({ value, onChange }) {
  return (
    <div role="radiogroup" aria-label="Cabeçalho" className="flex rounded-full bg-black/[.06] p-1 dark:bg-white/[.06]">
      {HEADERS.map((h) => (
        <button
          key={h.id}
          type="button"
          role="radio"
          aria-checked={value === h.id}
          onClick={() => onChange(h.id)}
          className={cn(
            "min-h-[36px] flex-1 rounded-full px-3 text-sm transition-colors",
            value === h.id ? "bg-white font-semibold text-foreground shadow-sm dark:bg-white/15" : "text-muted-foreground",
          )}
        >
          {h.label}
        </button>
      ))}
    </div>
  );
}

const norm = (s) => s.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

/**
 * The message as the client reads it: each [Variável] becomes its sample,
 * the firm's own name for [Nome do escritório]. Unknown ones stay as typed;
 * the server says why when creating.
 */
export function previewText(text, variables, firmName) {
  const byLabel = new Map(variables.map((v) => [norm(v.label), v.label === "Nome do escritório" && firmName ? firmName : v.sample]));
  return text.replace(/\[([^\]\n]{1,40})\]/g, (match, label) => byLabel.get(norm(label)) ?? match);
}

function Label({ children, htmlFor }) {
  return (
    <label htmlFor={htmlFor} className="mb-2 block text-sm font-semibold text-foreground">
      {children}
    </label>
  );
}

export default function AutomationDialog({ accountId, workflow, firmName, onClose, onCreated, onUnsupported }) {
  const copy = COPY[workflow];
  const { data: setup, isLoading, error: loadError } = useQuery({
    queryKey: automationSetupQueryKey(accountId, workflow),
    queryFn: () => fetchAutomationSetup(accountId, workflow),
  });

  const [channelId, setChannelId] = useState(null);
  const [presetId, setPresetId] = useState(null);
  const [text, setText] = useState(null);
  const textRef = useRef(null);
  const [header, setHeader] = useState("none");
  const [file, setFile] = useState(null);
  const [taskTypeIds, setTaskTypeIds] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (setup && !setup.supported) onUnsupported();
  }, [setup, onUnsupported]);

  const channels = setup?.channels || [];
  const presets = setup?.presets || [];
  const preset = presets.find((p) => p.id === presetId) || presets[0] || null;
  const chosenChannel = channelId || (channels.length === 1 ? channels[0].id : null);
  const taskTypes = setup?.taskTypes || [];
  const selectedTypes = taskTypeIds ?? taskTypes.filter((t) => t.hearing).map((t) => t.id);
  const headerSpec = HEADERS.find((h) => h.id === header);

  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => previewUrl && URL.revokeObjectURL(previewUrl), [previewUrl]);

  const variables = setup?.variables || [];
  const message = text ?? preset?.text ?? "";
  const edited = Boolean(preset) && message !== preset.text;
  const template = useMemo(() => {
    if (!preset) return null;
    const components = [];
    if (header !== "none") components.push({ type: "HEADER", format: header === "image" ? "IMAGE" : "VIDEO", previewUrl });
    components.push({ type: "BODY", text: previewText(message, variables, firmName) });
    return { components };
  }, [preset, header, previewUrl, message, variables, firmName]);

  /** Puts [Variável] where the cursor is, so the firm does not type brackets. */
  const insertVariable = (label) => {
    const el = textRef.current;
    const token = `[${label}]`;
    const start = el?.selectionStart ?? message.length;
    const end = el?.selectionEnd ?? message.length;
    const next = message.slice(0, start) + token + message.slice(end);
    setText(next);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const create = useMutation({
    mutationFn: () =>
      createQuickAutomation(accountId, workflow, {
        channelId: chosenChannel,
        presetId: preset.id,
        text: message,
        header,
        file,
        taskTypeIds: workflow === "reminder" ? selectedTypes : undefined,
      }),
    onSuccess: (res) => onCreated?.(res),
  });

  const missing =
    !chosenChannel ? "Escolha o WhatsApp que envia." :
    !message.trim() ? "Escreva a mensagem." :
    header !== "none" && !file ? `Escolha ${header === "image" ? "a imagem" : "o vídeo"}.` :
    workflow === "reminder" && !selectedTypes.length ? "Escolha pelo menos um tipo de compromisso." :
    null;

  const done = create.data;
  const typeOptions = taskTypes.map((t) => ({ value: t.id, label: t.name }));
  const typesSummary = taskTypes.filter((t) => selectedTypes.includes(t.id)).map((t) => t.name).join(", ");

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[92vh] w-[95vw] overflow-y-auto border-black/[.08] bg-background p-0 sm:max-w-[820px] dark:border-white/10">
        <div className="flex flex-wrap gap-6 p-6">
          <div className="min-w-0 flex-[999_1_340px]">
            <DialogHeader className="mb-5 text-left">
              <DialogTitle className="text-xl">{copy.title}</DialogTitle>
              <DialogDescription className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                {setup?.schedule || "Às 8h"}
              </DialogDescription>
            </DialogHeader>

            {isLoading || (setup && !setup.supported) ? (
              <div className="flex min-h-[220px] items-center justify-center" aria-busy="true">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
              </div>
            ) : loadError ? (
              <p role="alert" className="text-sm text-rose-700 dark:text-rose-300">{loadError.message || "Não foi possível abrir agora."}</p>
            ) : done ? (
              <div className="flex flex-col items-start gap-3 py-4" role="status">
                <span aria-hidden="true" className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                  <Check className="h-5 w-5" strokeWidth={2.5} />
                </span>
                <h3 className="text-lg font-semibold text-foreground">
                  {done.status === "active" ? copy.on : "Modelo enviado para a Meta"}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {done.status === "active"
                    ? "Começa a enviar no próximo horário."
                    : `A Meta revisa o modelo antes do primeiro envio. ${copy.waits}`}
                </p>
                <PrimaryButton onClick={onClose} className="mt-2 min-h-[44px] px-6 text-sm">
                  Fechar
                </PrimaryButton>
              </div>
            ) : (
              <div className="flex flex-col gap-5">
                {channels.length > 1 && (
                  <div>
                    <Label htmlFor="automation-channel">WhatsApp que envia</Label>
                    <Select value={chosenChannel || undefined} onValueChange={setChannelId}>
                      <SelectTrigger id="automation-channel" aria-label="WhatsApp que envia" className="min-h-[44px] rounded-xl">
                        <SelectValue placeholder="Escolha o número" />
                      </SelectTrigger>
                      <SelectContent>
                        {channels.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <label htmlFor="automation-text" className="text-sm font-semibold text-foreground">
                      Mensagem
                    </label>
                    <div role="radiogroup" aria-label="Modelo da mensagem" className="flex gap-1.5">
                      {presets.map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          role="radio"
                          aria-checked={preset?.id === p.id}
                          onClick={() => {
                            setPresetId(p.id);
                            setText(null);
                          }}
                          className={cn(
                            "min-h-[32px] rounded-full border px-3 text-[13px] transition-colors",
                            preset?.id === p.id
                              ? "border-foreground bg-foreground font-semibold text-background"
                              : "border-black/[.12] text-muted-foreground hover:text-foreground dark:border-white/15",
                          )}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <textarea
                    id="automation-text"
                    ref={textRef}
                    rows={4}
                    value={message}
                    onChange={(e) => setText(e.target.value)}
                    className="w-full resize-y rounded-2xl border border-black/[.1] bg-transparent px-4 py-3 text-[15px] leading-relaxed text-foreground dark:border-white/15"
                  />
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className="mr-1 text-[13px] text-muted-foreground">Inserir:</span>
                    {variables.map((v) => (
                      <button
                        key={v.label}
                        type="button"
                        onClick={() => insertVariable(v.label)}
                        className="min-h-[30px] rounded-full bg-violet-50 px-2.5 text-[13px] font-medium text-violet-800 hover:bg-violet-100 dark:bg-violet-500/15 dark:text-violet-200 dark:hover:bg-violet-500/25"
                      >
                        + {v.label}
                      </button>
                    ))}
                    {edited && (
                      <button
                        type="button"
                        onClick={() => setText(null)}
                        className="ml-auto min-h-[30px] text-[13px] text-muted-foreground underline-offset-4 hover:underline"
                      >
                        Voltar ao texto do modelo
                      </button>
                    )}
                  </div>
                  <p className="mt-1.5 text-[12.5px] text-muted-foreground">
                    O que está entre colchetes vem do sistema jurídico na hora do envio.
                  </p>
                </div>

                <div>
                  <p className="mb-2 text-sm font-semibold text-foreground">Em cima do texto</p>
                  <Segmented
                    value={header}
                    onChange={(h) => {
                      setHeader(h);
                      setFile(null);
                    }}
                  />
                  {headerSpec?.accept && (
                    <div className="mt-2.5 flex items-center gap-3">
                      <SecondaryButton onClick={() => fileRef.current?.click()} className="min-h-[40px] gap-2">
                        <headerSpec.icon className="h-4 w-4" aria-hidden="true" />
                        {file ? "Trocar" : header === "image" ? "Escolher imagem" : "Escolher vídeo"}
                      </SecondaryButton>
                      <span className="min-w-0 truncate text-[13px] text-muted-foreground">{file ? file.name : headerSpec.hint}</span>
                      <input
                        ref={fileRef}
                        type="file"
                        accept={headerSpec.accept}
                        className="sr-only"
                        aria-label={header === "image" ? "Imagem do cabeçalho" : "Vídeo do cabeçalho"}
                        onChange={(e) => {
                          setFile(e.target.files?.[0] || null);
                          e.target.value = "";
                        }}
                      />
                    </div>
                  )}
                </div>

                {workflow === "reminder" && (
                  <div>
                    <p className="mb-2 text-sm font-semibold text-foreground">Compromissos lembrados</p>
                    <div className="flex items-center justify-between gap-3 rounded-xl border border-black/[.1] px-3.5 py-2.5 dark:border-white/15">
                      <span className={cn("min-w-0 truncate text-sm", typesSummary ? "text-foreground" : "text-muted-foreground")}>
                        {typesSummary || (taskTypes.length ? "Nenhum escolhido" : "O sistema jurídico não trouxe os tipos")}
                      </span>
                      {taskTypes.length > 0 && (
                        <MultiSelectPopover
                          align="end"
                          className="w-72"
                          options={typeOptions}
                          selected={selectedTypes}
                          onChange={setTaskTypeIds}
                          searchable={typeOptions.length > 8}
                          trigger={
                            <button type="button" aria-label="Tipos de compromisso" className="shrink-0 text-sm font-semibold text-foreground underline-offset-4 hover:underline">
                              Trocar
                            </button>
                          }
                        />
                      )}
                    </div>
                  </div>
                )}

                {create.error && (
                  <p role="alert" className="flex items-start gap-2 rounded-xl bg-rose-50 px-3.5 py-2.5 text-sm text-rose-800 dark:bg-rose-500/10 dark:text-rose-200">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                    {create.error.message}
                  </p>
                )}

                <div className="flex flex-wrap items-center justify-end gap-3 border-t border-black/[.06] pt-4 dark:border-white/[.08]">
                  {missing && <span className="mr-auto text-[13px] text-muted-foreground">{missing}</span>}
                  <SecondaryButton onClick={onClose} disabled={create.isPending}>
                    Cancelar
                  </SecondaryButton>
                  <PrimaryButton onClick={() => create.mutate()} disabled={Boolean(missing) || create.isPending} className="min-h-[44px] px-6 text-sm">
                    {create.isPending ? "Criando..." : copy.create}
                  </PrimaryButton>
                </div>
              </div>
            )}
          </div>

          <aside aria-label="Prévia da mensagem" className="mx-auto flex flex-[0_0_260px] flex-col items-center">
            {template && <WhatsAppPhone template={template} values={{}} businessName={firmName || "Seu escritório"} width="260px" />}
          </aside>
        </div>
      </DialogContent>
    </Dialog>
  );
}
